import styled, { css, keyframes } from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Textfield } from "@similarweb/ui-components";

import { FakeInputContainer, subtitleFadeIn } from "pro-features/components/Workspace/Wizard/src/steps/StyledComponents";
import { ESelectedDomainErrorType } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

export const Wrapper = styled.div`
    width: 100%;
`;

const slideDown = keyframes`
    0% {
        transform: translateY(-80px);
    }
    100% {
        transform: translateY(0px);
    }
`;

export const ErrorBorderColorMap = {
    [ESelectedDomainErrorType.UPGRADE_ERROR]: colorsPalettes.mint[400],
    [ESelectedDomainErrorType.SENSITIVE_DATA]: colorsPalettes.red[400],
    [ESelectedDomainErrorType.NOT_ENOUGH_DATA]: colorsPalettes.red[400],
    [ESelectedDomainErrorType.WARNING]: colorsPalettes.orange[300],
};

const ErrorMessageColorMap = {
    [ESelectedDomainErrorType.UPGRADE_ERROR]: colorsPalettes.mint[500],
    [ESelectedDomainErrorType.SENSITIVE_DATA]: colorsPalettes.red[400],
    [ESelectedDomainErrorType.NOT_ENOUGH_DATA]: colorsPalettes.red[400],
    [ESelectedDomainErrorType.WARNING]: colorsPalettes.orange[500],
};

export const LegendContainer = styled.div`
    height: 64px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    animation: ${slideDown} ease-in-out 270ms;
    transition:
        opacity ease-in-out 120ms,
        transform ease-in-out 350ms;
    opacity: 1;
    transform: translateY(0px);
`;

export const WizardContainer = styled.div`
    height: 100%;

    ${Wrapper} {
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    ${LegendContainer} {
        flex: none;
    }
`;

export const SegmentRulesContainer = styled.div`
    border-radius: 4px 4px 0px 0px;
    box-shadow: 0px 3px 6px rgba(14, 30, 62, 0.08);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    flex: auto;
    overflow: hidden;
    width: 100%;
    background-color: ${colorsPalettes.carbon[0]};

    @media screen and (max-width: 1280px) {
        overflow: auto;
    }
`;

export const SegmentRulesSectionContainer = styled.div<{ disabled: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    flex: auto;
    padding: 0px;
    margin-bottom: 0px;
    box-sizing: border-box;
    overflow: hidden;
    ${({ disabled }) => (disabled ? "opacity: 0.3;" : "")};
`;

export const ContentWrapper = styled.div`
    display: flex;
    justify-content: center;
    column-gap: 40px;
    margin-left: 40px;
    height: 100%;
`;

export const RuleBuilderContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    flex: auto;
    align-items: flex-start;
    max-width: 1180px;
`;

const RulesQueryBuilderContainer = styled.div`
    padding: 0;
    flex: auto;
    overflow: auto;

    @media screen and (max-width: 1280px) {
        margin-bottom: 24px;
    }

    @media screen and (min-width: 1281px) {
        margin-bottom: 8px;
        padding-bottom: 64px;
    }
`;

export const SegmentRulesColumnContainer = styled.div`
    justify-content: center;
    width: 100%;
    display: flex;
`;

const titleFadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
 `;

export const Title = styled.div`
    ${setFontVariant("h2", colorsPalettes.carbon[500])};
    animation: ${titleFadeIn} ease-in-out 350ms;
    margin-bottom: 16px;
    line-height: 40px;
`;
Title.displayName = "Title";

export const BackButtonTextContainer = styled.span`
    width: 34px;
`;
BackButtonTextContainer.displayName = "BackButtonTextContainer";

export const BackButtonContainer = styled.div`
    margin-top: 20px;
    box-sizing: border-box;
    padding: 0;
    transition: all 0.2s ease-in;

    button {
        margin-left: -16px;

        .SWReactIcons {
            margin-right: 2px;
        }
    }
`;
BackButtonContainer.displayName = "BackButtonContainer";

export const SegmentRulesVerifyResultsContainer = styled(FlexColumn)`
    box-shadow: 0px 18px 34px rgba(14, 30, 62, 0.12);
    background-color: ${colorsPalettes.carbon[0]};
`;
SegmentRulesVerifyResultsContainer.displayName = "SegmentRulesVerifyResultsContainer";

export const DomainSelectionWrapper = styled.div`
    max-width: 1132px;
    margin: 8px 24px 16px 24px;
    animation: ${subtitleFadeIn} ease-in-out 1000ms;
`;
DomainSelectionWrapper.displayName = "DomainSelectionWrapper";

export const SegmentRulesTitle = styled.div`
    ${setFontVariant("h6", colorsPalettes.carbon[500])};
    margin-top: 16px;
    margin-left: 24px;
    height: 24px;
    line-height: 24px;
    white-space: nowrap;
`;
SegmentRulesTitle.displayName = "SegmentRulesTitle";

export const SegmentRulesStepTopContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    flex: auto;
    overflow: hidden;
    border-top: 1px solid ${colorsPalettes.navigation.BORDER_DARK_1};

    @media screen and (max-width: 1280px) {
        flex-direction: column;
        align-items: normal;
        height: auto;
        overflow: visible;
    }
`;
SegmentRulesStepTopContainer.displayName = "SegmentRulesStepTopContainer";

export const FakeInputContainerStyled = styled(FakeInputContainer)<{
    errorType: ESelectedDomainErrorType;
}>`
    border: 1px solid ${colorsPalettes.carbon["100"]};
    ${({ errorType }) =>
        typeof errorType === "number" &&
        css`
            border-color: ${ErrorBorderColorMap[errorType]};
        `};
    border-radius: 3px;
    padding: 2px 0px;
`;
FakeInputContainerStyled.displayName = "FakeInputContainerStyled";

export const ErrorLabel = styled.span<{
    errorType: ESelectedDomainErrorType;
}>`
    ${({ errorType }) =>
        typeof errorType === "number" &&
        css`
            color: ${ErrorMessageColorMap[errorType]};
        `};
    display: flex;
    margin-top: 12px;
`;
ErrorLabel.displayName = "ErrorLabel";

export const StyledIcon = styled(SWReactIcons)<{
    isTabIcon?: boolean;
    isListItemIcon?: boolean;
    isLocked?: boolean;
}>`
    width: 24px;
    height: 24px;
    margin-left: 10px;
    margin-top: ${({ isListItemIcon, isTabIcon, isLocked }) =>
        isLocked ? "12px" : isListItemIcon ? "18px" : isTabIcon ? "0px" : "18px"};
    margin-bottom: ${({ isTabIcon }) => (isTabIcon ? "21px" : "")};
    ${({ isListItemIcon, isTabIcon }) => (isListItemIcon || isTabIcon ? "position: absolute; right: 18px;" : "")};
    ${({ isTabIcon }) => (isTabIcon ? "top: 13px;" : "")};
`;
StyledIcon.displayName = "StyledIcon";

export const FiltersTitleWrapper = styled.div<{
    disabled: boolean;
    addFiltersTitleShadow: boolean;
}>`
    display: inline-flex;
    padding-bottom: 14px;
    justify-content: space-between;
    ${({ disabled }) => (disabled ? "opacity: 0.3;" : "")};
    transition:
        box-shadow 2s,
        transform 2s ease-in-out;
    ${({ addFiltersTitleShadow }) => (addFiltersTitleShadow ? "box-shadow: 0px 0px 22px rgba(14, 30, 62, 0.08);" : "")};
`;
FiltersTitleWrapper.displayName = "FiltersTitleWrapper";

export const FlexedDiv = styled.div`
    display: flex;
`;
FlexedDiv.displayName = "FlexedDiv";

export const ClearAllButtonWrapper = styled.div<{ disabled: boolean }>`
    height: 36px;
    margin-right: 24px;
    margin-top: 10px;
    .Button div {
        ${setFontVariant("tab")};
        color: ${({ disabled }) => (disabled ? colorsPalettes.carbon[200] : colorsPalettes.carbon[500])};
    }
`;
ClearAllButtonWrapper.displayName = "ClearAllButtonWrapper";

export const PagesSeparatorWrapper = styled.div`
    text-align: center;
    line-height: 16px;
    margin-top: 12px;
    margin-bottom: 12px;
    font-weight: 500;
`;
PagesSeparatorWrapper.displayName = "PagesSeparatorWrapper";

export const BoldSpan = styled.span`
    color: ${colorsPalettes.carbon[500]};
`;
BoldSpan.displayName = "BoldSpan";

export const SeparatorTextSpan = styled.span`
    color: ${colorsPalettes.carbon[250]};
`;
SeparatorTextSpan.displayName = "SeparatorTextSpan";

export const CircleSeparator = styled.span`
    background-color: ${colorsPalettes.carbon[250]};
    display: inline-block;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    margin-bottom: 4px;
    margin-left: 4px;
    margin-right: 4px;
`;
CircleSeparator.displayName = "CircleSeparator";

export const SegmentRulesScrollAreaContainer = styled.div`
    flex: auto;
    overflow: hidden;
    z-index: 0;

    .ScrollArea {
        height: 100%;
    }
`;
SegmentRulesScrollAreaContainer.displayName = "SegmentRulesScrollAreaContainer";

const ModalContentWrapper = styled(FlexColumn)`
    width: 100%;
`;
ModalContentWrapper.displayName = "ModalContentWrapper";

export const CreateButtonContainer = styled(FlexColumn)`
    position: absolute;
    bottom: 0;
    right: 0;
    width: 360px;
    height: 160px;
    justify-content: end;
    background-image: linear-gradient(to bottom, transparent 0%, ${colorsPalettes.carbon["0"]} 100%);
    z-index: 100;
`;
CreateButtonContainer.displayName = "CreateButtonContainer";

const gentlePulse = keyframes`
    0% { transform: scale(1); opacity: 0.7; }
    50% { transform: scale(1.04); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
`;

export const CreateButtonWrapper = styled.div<{ isEnabled?: boolean }>`
    width: 100%;
    height: 57px;
    bottom: 0;
    display: flex;
    background-color: ${colorsPalettes.carbon["0"]};
    justify-content: center;
    z-index: 100;
    .SWReactIcons {
        margin-left: 8px;
        svg {
            path {
                fill: ${colorsPalettes.carbon["0"]};
            }
        }
    }
    &:hover {
        .SWReactIcons {
            margin-left: 8px;
            svg {
                path {
                    fill: ${colorsPalettes.carbon["0"]};
                }
            }
        }
    }
    ${({ isEnabled }) =>
        isEnabled &&
        css`
            > button {
                animation: ${gentlePulse} 600ms ease-in-out;
            }
        `}
`;
CreateButtonWrapper.displayName = "CreateButtonWrapper";

export const TextFieldStyled = styled(Textfield)`
    height: 50px;
    margin-top: 16px;
    padding-right: 8px;
    width: 352px;
`;
